import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { SearchOutlined } from '@ant-design/icons';
import { Modal, RadioChangeEvent } from 'antd';
import styled from 'styled-components';

import { getCommunitiesEmailNotificationRecipients } from 'apis/CommunityAPI';
import Avatar from 'common/Avatar';
import Checkbox from 'components/lib/Checkbox';
import Input from 'components/lib/Input';
import Loader from 'components/lib/Loader';
import Radio from 'components/lib/Radio';
import Select from 'components/lib/Select';
import { CommunityPreferenceNotification, CommunityPreferences, CommunityPreferenceType } from 'types/communities';
import { useDebounce } from 'utils/hooks/useDebounce';

import { ACTOR_TYPE } from '../../../constants';
import { SETTINGS_PAGE_INFO, SHOW_RECIPIENTS_OPTIONS } from '../constants';
import SettingsModal from '../SettingsModal';

const EmailNotificationsModalWrapper = styled(SettingsModal)`
  .ant-modal-content {
    .ant-modal-header {
      border-bottom: none;
    }
  }
`;

const Content = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Settings = styled.div`
  padding-top: 10px;
  padding-right: 20px;
  min-height: 350px;
  max-width: 442px;
`;

const Recipients = styled.div`
  margin-bottom: 15px;
`;

const RecipientsHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ShowFilter = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
  font-size: 12px;
`;

const ShowLabel = styled.div`
  font-family: var(--font-bold);
`;

const StyledCheckbox = styled(Checkbox)`
  margin-top: 10px;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 900;
  padding-left: 15px;
  display: flex;

  .ant-checkbox-label {
    display: flex;
  }

  .ant-checkbox-input {
    width: 18px;
    height: 18px;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: var(--positive-green);
    border-color: var(--positive-green);
  }
`;

const CommunityName = styled.div`
  background-color: #ecedee;
  font-size: 10px;
  font-weight: 900;
  height: 20px;
  display: flex;
  align-items: center;
  padding-left: 15px;
`;

const RecipientDetails = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  font-size: 12px;
  font-weight: 500;
  margin-left: 5px;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const StyledSelect = styled(Select)`
  && .ant-select-selector {
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    padding-right: 8px !important;
    color: var(--dark-blue);
  }

  &&.ant-select-open .ant-select-selector,
  &&.ant-select-focused .ant-select-selector {
    box-shadow: none !important;
  }

  && .ant-select-selection-item,
  && .ant-select-selection-placeholder {
    font-size: 12px !important;
  }

  &.ant-select-show-arrow {
    margin-right: -8px;
  }
`;

const ShowFilterDropdown = styled.div`
  font-size: 12px;

  .ant-select-item,
  .ant-select-item-option-content {
    font-size: 12px;
  }
`;

type Props = {
  isOpen: boolean;
  loading: boolean;
  title: string;
  onClose: () => void;
  communitiesToModify: CommunityPreferenceType[];
  settingsPageType: string;
  onSave: (
    newStatus: number,
    preferenceNotifications: CommunityPreferenceNotification[],
    extraSettings?: any
  ) => void;
  recipientsNotificationType?: number;
  showRecipients?: boolean;
  getInitialStatusValue?: (args: { communitiesToModify: CommunityPreferenceType[]; currentSetting: any }) => number;
  width?: number;
  renderSettingsSection?: (args: {
    value: number | undefined;
    setStatusValue: (nextStatus: number) => void;
    communitiesToModify: CommunityPreferenceType[];
    currentSetting: any;
    modalDescription: string;
    isOpen: boolean;
  }) => React.ReactNode;
};

const EmailNotificationsRecipientsModal = ({
  onClose,
  onSave,
  isOpen,
  loading,
  title,
  communitiesToModify,
  settingsPageType,
  recipientsNotificationType,
  showRecipients,
  getInitialStatusValue,
  width,
  renderSettingsSection,
}: Props) => {
  const [value, setValue] = useState<number | undefined>();
  const [allCommunities, setAllCommunities] = useState<CommunityPreferenceNotification[]>([]);
  const [currentPageCommunities, setCurrentPageCommunities] = useState<CommunityPreferenceNotification[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [recipientsFilter, setRecipientsFilter] = useState<number>(0);

  const debouncedSearch = useDebounce(searchTerm);
  const currentSetting = SETTINGS_PAGE_INFO[settingsPageType];
  const effectiveRecipientsNotificationType = recipientsNotificationType ?? currentSetting.type;
  const modalDescription = Object.prototype.hasOwnProperty.call(currentSetting, 'recipientsModalDescription')
    ? currentSetting.recipientsModalDescription
    : currentSetting.description;
  const setStatusValue = useCallback((nextStatus: number) => setValue(nextStatus), []);

  const ids = communitiesToModify.map((c) => c.id);
  const shouldRenderRecipients = showRecipients ?? value !== currentSetting.statusElements.OFF.value;

  const { isLoading, isFetching, refetch } = useQuery(
    ['emailNotificationRecipients', ids, debouncedSearch, effectiveRecipientsNotificationType],
    async () => {
      return await getCommunitiesEmailNotificationRecipients(effectiveRecipientsNotificationType, {
        communities: ids,
        search: debouncedSearch,
      });
    },
    {
      enabled: isOpen && communitiesToModify.length > 0,
      onSuccess: (data) => {
        setAllCommunities((prev) => {
          const prevSelections = new Map<number, boolean>();
          prev.forEach((c) => c.users.forEach((u) => prevSelections.set(u.id, u.email_notifications_enabled)));

          const merged = [...prev];
          data.forEach((community: CommunityPreferenceNotification) => {
            const idx = merged.findIndex((c) => c.community_id === community.community_id);

            if (idx >= 0 && merged[idx]) {
              const existing = merged[idx];
              const usersMap = new Map<number, any>();

              // Keep all existing users (with previous states)
              existing?.users?.forEach((u) => {
                usersMap.set(u.id, {
                  ...u,
                  email_notifications_enabled: prevSelections.get(u.id) ?? u.email_notifications_enabled,
                });
              });

              // Merge from API
              community.users.forEach((u) => {
                usersMap.set(u.id, {
                  ...u,
                  email_notifications_enabled: prevSelections.has(u.id)
                    ? prevSelections.get(u.id)!
                    : u.email_notifications_enabled,
                });
              });

              merged[idx] = {
                ...existing,
                users: Array.from(usersMap.values()),
              };
            } else {
              merged.push({
                ...community,
                users: community.users.map((u) => ({
                  ...u,
                  email_notifications_enabled: prevSelections.has(u.id)
                    ? prevSelections.get(u.id)!
                    : u.email_notifications_enabled,
                })),
              });
            }
          });
          return merged;
        });

        setCurrentPageCommunities((prev) =>
          data.map((community: CommunityPreferenceNotification) => ({
            ...community,
            users: community.users.map((u) => {
              const existing = allCommunities
                .find((c) => c.community_id === community.community_id)
                ?.users.find((usr) => usr.id === u.id);
              return {
                ...u,
                email_notifications_enabled: existing?.email_notifications_enabled ?? u.email_notifications_enabled,
              };
            }),
          }))
        );
      },
    }
  );

  useEffect(() => {
    if (isOpen) {
      let numericPreference: number;

      if (getInitialStatusValue) {
        numericPreference = getInitialStatusValue({ communitiesToModify, currentSetting });
      } else {
        const fieldName = currentSetting.field as keyof CommunityPreferences;
        const preferenceValue = communitiesToModify[0]?.preferences?.[fieldName];
        numericPreference = Number(preferenceValue ?? 0);
      }

      setValue(Number.isNaN(numericPreference) ? 0 : numericPreference);
      refetch();
    } else {
      setSearchTerm('');
      setAllCommunities([]);
      setCurrentPageCommunities([]);
      setValue(undefined);
      setRecipientsFilter(0);
    }
  }, [isOpen, communitiesToModify, refetch, currentSetting, getInitialStatusValue]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setAllCommunities([]);
    setCurrentPageCommunities([]);
    setRecipientsFilter(0);
    refetch();
  }, [effectiveRecipientsNotificationType, isOpen, refetch]);

  const displayedCommunities = useMemo(() => {
    if (recipientsFilter === 0) {
      return currentPageCommunities;
    }

    return currentPageCommunities
      .map((community) => ({
        ...community,
        users: community.users.filter((user) => user.email_notifications_enabled),
      }))
      .filter((community) => community.users.length > 0);
  }, [currentPageCommunities, recipientsFilter]);

  const allSelected =
    displayedCommunities.length > 0 &&
    displayedCommunities.every((c) => c.users.every((u) => u.email_notifications_enabled));

  const selectAllChange = (event: any) => {
    const checked = event.target.checked;
    const currentUserIdsByCommunity = new Map(
      displayedCommunities.map((community) => [community.community_id, new Set(community.users.map((user) => user.id))])
    );

    setAllCommunities((prev) =>
      prev.map((c) => {
        const userIds = currentUserIdsByCommunity.get(c.community_id);
        if (!userIds) return c;

        return {
          ...c,
          users: c.users.map((u) => (userIds.has(u.id) ? { ...u, email_notifications_enabled: checked } : u)),
        };
      })
    );

    setCurrentPageCommunities((prev) =>
      prev.map((c) => ({
        ...c,
        users: c.users.map((u) => ({ ...u, email_notifications_enabled: checked })),
      }))
    );
  };

  const handleRecipientChange = (communityId: number, userId: number, checked: boolean) => {
    setAllCommunities((prev) =>
      prev.map((c) =>
        c.community_id === communityId
          ? {
              ...c,
              users: c.users.map((u) => (u.id === userId ? { ...u, email_notifications_enabled: checked } : u)),
            }
          : c
      )
    );
    setCurrentPageCommunities((prev) =>
      prev.map((c) =>
        c.community_id === communityId
          ? {
              ...c,
              users: c.users.map((u) => (u.id === userId ? { ...u, email_notifications_enabled: checked } : u)),
            }
          : c
      )
    );
  };

  const saveChanges = () => {
    if (value === undefined) return;

    if (value !== currentSetting.statusElements.OFF.value) {
      const communitiesWithoutRecipients = allCommunities.filter(
        (c) => c.users.length > 0 && !c.users.some((u) => u.email_notifications_enabled)
      );

      if (communitiesWithoutRecipients.length > 0) {
        Modal.info({
          title: <div style={{ fontFamily: 'var(--font-regular)' }}>Recipients Missing</div>,
          content: (
            <div style={{ fontFamily: 'var(--font-regular)' }}>
              You need to select at least one recipient per community.
            </div>
          ),
          okText: 'OK',
          centered: true,
        });
        return;
      }
    }

    onSave(value, allCommunities);
  };

  const renderRecipients = () => {
    if (isLoading || isFetching) {
      return (
        <LoaderWrapper>
          <Loader active />
        </LoaderWrapper>
      );
    }

    if (displayedCommunities.length === 0) {
      return (
        <span style={{ color: 'var(--text-secondary)' }}>
          {recipientsFilter === 1 ? 'No recipients selected' : 'No recipients found'}
        </span>
      );
    }

    return [...displayedCommunities]
      .sort((a, b) => a.community_name.localeCompare(b.community_name))
      .map((community) => (
        <div key={community.community_id} style={{ marginBottom: 20 }}>
          <CommunityName>{community.community_name}</CommunityName>
          {community.users.length > 0 ? (
            community.users.map((user) => (
              <div key={user.id} style={{ marginBottom: 8 }}>
                <StyledCheckbox
                  checked={user.email_notifications_enabled}
                  onChange={(e: any) => handleRecipientChange(community.community_id, user.id, e.target.checked)}
                >
                  <Avatar size={18} name={user.name} type={ACTOR_TYPE.USER} src={user.avatar} />
                  <RecipientDetails>
                    <span>{user.name}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{user.email}</span>
                  </RecipientDetails>
                </StyledCheckbox>
              </div>
            ))
          ) : (
            <div style={{ color: 'var(--text-secondary)', paddingLeft: '15px', marginTop: '10px' }}>
              No available recipients found
            </div>
          )}
        </div>
      ));
  };

  return (
    <EmailNotificationsModalWrapper
      title={title}
      onClose={onClose}
      onSave={saveChanges}
      isOpen={isOpen}
      disabled={loading}
      communitiesToModify={communitiesToModify}
      width={width}
    >
      <Content>
        {renderSettingsSection ? (
          renderSettingsSection({
            value,
            setStatusValue,
            communitiesToModify,
            currentSetting,
            modalDescription,
            isOpen,
          })
        ) : (
          <Settings>
            <span style={{ fontSize: '14px' }}>{modalDescription}</span>
            <Radio.Group
              style={{
                display: 'flex',
                flexDirection: 'row',
                marginTop: 15,
              }}
              value={value}
              onChange={(e: RadioChangeEvent) => {
                setValue(e.target.value);
              }}
            >
              {Object.values(currentSetting.statusElements)
                .sort((a: any, b: any) => Number(a.value) - Number(b.value))
                .map((status: any) => (
                  <Radio className={'large'} key={status.value} value={status.value}>
                    {status.label}
                  </Radio>
                ))}
            </Radio.Group>
          </Settings>
        )}
        {shouldRenderRecipients && (
          <Recipients>
            <Input
              prefix={<SearchOutlined />}
              style={{ width: 436 }}
              placeholder={communitiesToModify.length > 1 ? 'Search users and communities' : 'Search recipients'}
              value={searchTerm}
              onChange={(e: any) => setSearchTerm(e.target.value)}
            />

            <RecipientsHeader>
              <StyledCheckbox checked={allSelected} onChange={selectAllChange}>
                Recipients
              </StyledCheckbox>
              <ShowFilter>
                <ShowLabel>Show</ShowLabel>
                <StyledSelect
                  value={recipientsFilter}
                  options={SHOW_RECIPIENTS_OPTIONS}
                  onChange={(filterValue) => setRecipientsFilter(filterValue)}
                  dropdownMatchSelectWidth={false}
                  dropdownRender={(menu) => <ShowFilterDropdown>{menu}</ShowFilterDropdown>}
                />
              </ShowFilter>
            </RecipientsHeader>
            <div className="mini-scroll" style={{ height: 330, overflowY: 'auto' }}>
              {renderRecipients()}
            </div>
          </Recipients>
        )}
      </Content>
    </EmailNotificationsModalWrapper>
  );
};

export default EmailNotificationsRecipientsModal;
